import { z } from 'zod';

import { apiEnvelope } from '../envelope';
import { guardOutcomeSchema } from '../content-guard/outcome';
import { messageAuthorshipSchema } from '../conversations/chat';

/**
 * Which path produced a row's copy: the personalised prompt, the standard
 * template used when generation fails, or a reviewer who replaced the message
 * body by hand. Recorded on the row and carried on the outreach events so the
 * fallback rate is chartable.
 */
export const OUTREACH_CONTENT_SOURCE = ['personalised', 'template', 'human'] as const;
export const outreachContentSourceSchema = z.enum(OUTREACH_CONTENT_SOURCE);
export type OutreachContentSource = z.infer<typeof outreachContentSourceSchema>;

// Wire schemas and closed vocabularies for automated care-team outreach
// (`/api/v1/internal/outreach`). Safe for every bundle: no `../db/*` import.
// Member identity never travels in a request body — the staff caller is the
// verified internal-pool subject and the member is addressed by the row id.
// Authorship is the conversations vocabulary, exported from its own block.

/**
 * Row lifecycle. `held_*` rows are queue-visible and never auto-send.
 * `failed_generation` is reserved and unreachable today: a generation failure
 * falls back to the standard copy rather than retiring the row, so no producer
 * writes it; the value stays so the CHECK and this enum agree. `text` + CHECK
 * on the DB side (ADR-056 Rule 8); this enum is the source.
 */
export const OUTREACH_STATUS = [
  'pending_review',
  'approved',
  'held_guard',
  'held_safety',
  'sent',
  'cancelled',
  'failed_generation',
] as const;
export const outreachStatusSchema = z.enum(OUTREACH_STATUS);
export type OutreachStatus = z.infer<typeof outreachStatusSchema>;
/** Statuses a reviewer may still act on. */
export const OUTREACH_REVIEWABLE_STATUS = [
  'pending_review',
  'approved',
  'held_guard',
  'held_safety',
] as const satisfies readonly OutreachStatus[];
/** Statuses the sweeper may claim and deliver. */
export const OUTREACH_CLAIMABLE_STATUS = [
  'pending_review',
  'approved',
] as const satisfies readonly OutreachStatus[];
/**
 * How long a sweeper's claim shuts reviewers and other sweepers out of a row.
 * A crashed delivery leaves the lease to expire, after which the row is due again.
 */
export const OUTREACH_CLAIM_LEASE_MS = 10 * 60 * 1000;

/**
 * Statuses a reviewer can see in the queue. `cancelled` is listed so a row the
 * system cancelled at send time stays auditable with its reason, bounded to the
 * window below rather than forever.
 */
export const OUTREACH_QUEUE_STATUS = [
  'pending_review',
  'approved',
  'held_guard',
  'held_safety',
  'cancelled',
] as const satisfies readonly OutreachStatus[];
/** How long a cancelled row stays in the queue after `cancelled_at`. */
export const OUTREACH_QUEUE_CANCELLED_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** Why the system cancelled a row at send time (never a human). */
export const OUTREACH_SYSTEM_CANCEL_REASON = [
  'incomplete_draft',
  'superseded_by_reply',
  'superseded_by_booking',
  'superseded_by_escalation',
  'superseded_by_staff',
  'assignment_ended',
  // The member was reassigned while the row waited (pending, approved or
  // held): the copy was written in the former specialist's name, so the
  // sweeper retires it rather than hand it to the new one.
  'superseded_by_reassignment',
  'care_chat_denied',
  'dry_run',
  // The send kept failing for reasons that are not the member's: the row is
  // retired after a bounded number of attempts instead of retrying forever.
  'send_failed',
] as const;

/**
 * Where a `care.outreach.message.failed` event came from: the model could not
 * produce usable copy (the member still gets the standard copy), or a send
 * attempt on a finished row failed (retried, then cancelled `send_failed`).
 */
export const OUTREACH_FAILURE_STAGE = ['generation', 'send'] as const;
export const outreachFailureStageSchema = z.enum(OUTREACH_FAILURE_STAGE);
export type OutreachFailureStage = z.infer<typeof outreachFailureStageSchema>;

/** Why a reviewer cancelled a row. Closed so the queue offers a picker, not free text. */
export const OUTREACH_REVIEW_CANCEL_REASON = [
  'not_appropriate',
  'already_in_contact',
  'member_asked_no_contact',
  'other',
] as const;
export const outreachReviewCancelReasonSchema = z.enum(OUTREACH_REVIEW_CANCEL_REASON);
export type OutreachReviewCancelReason = z.infer<typeof outreachReviewCancelReasonSchema>;

export const OUTREACH_CANCEL_REASON = [
  ...OUTREACH_SYSTEM_CANCEL_REASON,
  ...OUTREACH_REVIEW_CANCEL_REASON,
] as const;
export const outreachCancelReasonSchema = z.enum(OUTREACH_CANCEL_REASON);
export type OutreachCancelReason = z.infer<typeof outreachCancelReasonSchema>;

/**
 * The cancel reasons that may be charted. An escalation is a clinical-safety
 * signal and never reaches the behavioural plane (ADR-079 Rule 14): the
 * producer publishes it without its analytics annotation, and the forwarder
 * rejects the value outright so a producer that forgets to is dropped, not
 * charted. Spelled out rather than filtered so the type stays a literal tuple.
 */
export const OUTREACH_ANALYTICS_CANCEL_REASON = [
  'incomplete_draft',
  'superseded_by_reply',
  'superseded_by_booking',
  'superseded_by_staff',
  'assignment_ended',
  'superseded_by_reassignment',
  'care_chat_denied',
  'dry_run',
  'send_failed',
  ...OUTREACH_REVIEW_CANCEL_REASON,
] as const satisfies readonly Exclude<OutreachCancelReason, 'superseded_by_escalation'>[];
export const outreachAnalyticsCancelReasonSchema = z.enum(OUTREACH_ANALYTICS_CANCEL_REASON);

export const OUTREACH_REVIEW_ACTION = ['approve', 'send_now', 'edit', 'cancel'] as const;
export const outreachReviewActionSchema = z.enum(OUTREACH_REVIEW_ACTION);
export type OutreachReviewAction = z.infer<typeof outreachReviewActionSchema>;

/**
 * Producer outcomes — the closed `outcome` tag on `outreach.evaluated`. Every
 * candidate a tick evaluates lands in exactly one, so the outcomes sum to the
 * tick's evaluated count: the skips and the safety hold before generation,
 * drafted or the guard hold after it, and `failed` when the candidate's
 * evaluation threw and its shell was left for the stale-shell reclaim.
 */
export const OUTREACH_EVALUATION_OUTCOME = [
  'drafted',
  'skipped_unassigned',
  'skipped_in_care',
  'skipped_booked',
  'skipped_escalated',
  'skipped_age',
  'skipped_cohort',
  'held_safety',
  'held_guard',
  'failed',
] as const;
export type OutreachEvaluationOutcome = (typeof OUTREACH_EVALUATION_OUTCOME)[number];

/**
 * Why a send failed — the closed `failure_class` tag on `outreach.send_failed`.
 * A throw the sweeper cannot classify is `unclassified`; the error's class name
 * goes on the log line beside it, never on the tag (ADR-069).
 */
export const OUTREACH_SEND_FAILURE_CLASS = [
  'care_chat_unavailable',
  'unclassified',
  'push_enqueue',
  'tick_error',
] as const;
export type OutreachSendFailureClass = (typeof OUTREACH_SEND_FAILURE_CLASS)[number];

/**
 * Length bounds count code points, the same unit the content guard shortens
 * by: a `max()` counts UTF-16 units and would reject an emoji the guard passed.
 */
const withinCodePoints = (max: number) => (value: string) => [...value].length <= max;

/** Caps on the copy a reviewer may save, in code points; the Atlas editor mirrors them. */
export const OUTREACH_MESSAGE_MAX_CHARS = 2000;
export const OUTREACH_PUSH_TITLE_MAX_CHARS = 60;
export const OUTREACH_PUSH_BODY_MAX_CHARS = 160;

/** Push copy as previewed and as sent — bounded, never a message body. */
// Parsed by the Atlas client as part of a response, so unknown keys are
// stripped rather than rejected (ADR-135 Rule 1); the edit request re-uses it
// inside a strict body, where the outer object rejects unknown keys.
export const outreachPushCopySchema = z.object({
  title: z
    .string()
    .trim()
    .min(1)
    .refine(withinCodePoints(OUTREACH_PUSH_TITLE_MAX_CHARS), {
      message: `at most ${OUTREACH_PUSH_TITLE_MAX_CHARS} characters`,
    }),
  body: z
    .string()
    .trim()
    .min(1)
    .refine(withinCodePoints(OUTREACH_PUSH_BODY_MAX_CHARS), {
      message: `at most ${OUTREACH_PUSH_BODY_MAX_CHARS} characters`,
    }),
});
export type OutreachPushCopy = z.infer<typeof outreachPushCopySchema>;

/**
 * Caps on the member display, in code points — the unit the server cuts the
 * member-typed name in, so a name of astral characters the cut kept is never
 * rejected on read. Two for the initial: one character, or the two an upper-case
 * mapping can expand it to (`ß` → `SS`).
 */
export const OUTREACH_MEMBER_FIRST_NAME_MAX_CHARS = 80;
export const OUTREACH_MEMBER_INITIAL_MAX_CHARS = 2;

/**
 * Audience-safe member display for the staff queue: first name, an initial,
 * and the full preferred name. Derived server-side from the preferred-name port.
 */
export const outreachMemberDisplaySchema = z
  // An empty first name means no preferred name is known; the client renders its own catalog fallback.
  .object({
    firstName: z.string().refine(withinCodePoints(OUTREACH_MEMBER_FIRST_NAME_MAX_CHARS), {
      message: `at most ${OUTREACH_MEMBER_FIRST_NAME_MAX_CHARS} characters`,
    }),
    initial: z.string().refine(withinCodePoints(OUTREACH_MEMBER_INITIAL_MAX_CHARS), {
      message: `at most ${OUTREACH_MEMBER_INITIAL_MAX_CHARS} characters`,
    }),
    // Additive (ADR-135): full preferred name for the staff queue; empty string when no name is known.
    fullName: z.string(),
  });

/** Most context port keys one queue row lists; the server caps the projection to it. */
export const OUTREACH_REFERENCED_CONTEXT_MAX = 20;

/**
 * Whether a member's notifications reach them, as the queue shows it. `on`: a
 * granted platform with a push token on file; `off`: something was reported
 * but no granted platform has a token; `unknown`: the app never reported.
 * A closed enum so no token, device or platform detail reaches staff.
 */
export const OUTREACH_NOTIFICATION_STATE = ['on', 'off', 'unknown'] as const;
export const outreachNotificationStateSchema = z.enum(OUTREACH_NOTIFICATION_STATE);
export type OutreachNotificationState = z.infer<typeof outreachNotificationStateSchema>;

export const outreachQueueRowSchema = z.object({
  id: z.string().uuid(),
  // staff-only: memberId intentionally included for profile navigation (ADR-069 deliberate
  // tradeoff — Atlas is an internal staff surface, not member-facing; page-level session
  // replay policy via ADR-080 applies; no additional per-field masking required).
  memberId: z.string(),
  member: outreachMemberDisplaySchema,
  definitionKey: z.string().min(1),
  status: outreachStatusSchema,
  contentSource: outreachContentSourceSchema,
  authorship: messageAuthorshipSchema,
  guardOutcome: guardOutcomeSchema.nullable(),
  /** ISO-8601 UTC instant; null while a row is held or once it is cancelled (nothing to count down to). */
  sendAt: z.string().datetime({ offset: true }).nullable(),
  draftedAt: z.string().datetime({ offset: true }),
  message: z.string().nullable(),
  push: outreachPushCopySchema.nullable(),
  /** Context port keys the copy drew on — keys only, never rendered context. */
  referencedContext: z.array(z.string().min(1).max(40)).max(OUTREACH_REFERENCED_CONTEXT_MAX),
  // Additive: older clients strip them (ADR-135); present and null on rows not cancelled.
  cancelReason: outreachCancelReasonSchema.nullable().optional(),
  cancelledAt: z.string().datetime({ offset: true }).nullable().optional(),
  // Additive: an older Atlas strips it and an older API omits it; absent reads as unknown.
  notifications: outreachNotificationStateSchema.optional(),
});
export type OutreachQueueRow = z.infer<typeof outreachQueueRowSchema>;

/** Rows one queue page returns at most, and the page size when `limit` is absent. */
export const OUTREACH_QUEUE_PAGE_MAX = 200;
/** Wire bound on the opaque page cursor; a token this server issued is well under it. */
export const OUTREACH_QUEUE_CURSOR_MAX_LENGTH = 200;

/**
 * Queue list query, as the wire carries it (strings). Documents the contract;
 * the handler is the enforcing pass, so the bounds here mirror its rules.
 */
export const listOutreachQueueQuerySchema = z.object({
  /** Page size, `1..OUTREACH_QUEUE_PAGE_MAX`; the maximum when absent. */
  limit: z.string().regex(/^\d+$/).optional(),
  /** The `nextCursor` of the previous page; absent for the first page. */
  cursor: z.string().min(1).max(OUTREACH_QUEUE_CURSOR_MAX_LENGTH).optional(),
});

/**
 * One page of the queue in its fixed order (holds first, then live rows by
 * send time, then cancelled rows), plus the opaque cursor for the page after
 * it — `null` on the last page, so a client pages until it sees null.
 */
export const listOutreachQueueResponseSchema = apiEnvelope(
  z.object({ rows: z.array(outreachQueueRowSchema), nextCursor: z.string().nullable() }),
);
export const getOutreachRowResponseSchema = apiEnvelope(outreachQueueRowSchema);

// Approving always sends: a personalised draft approved without a send time
// sits unsent with nothing to tell the specialist, so only send-now is accepted.
export const approveOutreachRequestSchema = z.object({ sendNow: z.literal(true) }).strict();
export type ApproveOutreachRequest = z.infer<typeof approveOutreachRequestSchema>;

export const editOutreachRequestSchema = z
  .object({
    // Code points, not UTF-16 units, so the bound counts what the Atlas editor
    // counts and an astral character cannot pass the editor yet fail the route.
    message: z
      .string()
      .trim()
      .min(1)
      .refine(withinCodePoints(OUTREACH_MESSAGE_MAX_CHARS), {
        message: `message must be at most ${OUTREACH_MESSAGE_MAX_CHARS} characters`,
      })
      .optional(),
    // The response-tolerant push shape is reused, but a request rejects unknown nested keys too.
    push: outreachPushCopySchema.strict().optional(),
  })
  .strict()
  .refine((body) => body.message !== undefined || body.push !== undefined, {
    message: 'an edit must replace the message, the push copy, or both',
  });
export type EditOutreachRequest = z.infer<typeof editOutreachRequestSchema>;

export const cancelOutreachRequestSchema = z
  .object({ reason: outreachReviewCancelReasonSchema })
  .strict();
export type CancelOutreachRequest = z.infer<typeof cancelOutreachRequestSchema>;

/** ADR-002 stable error codes for the staff routes. */
export const OUTREACH_ERROR_CODE = {
  notFound: 'outreach_row_not_found',
  invalidState: 'outreach_invalid_state',
  denylistedCopy: 'outreach_denylisted_copy',
} as const;
